import { useState, useEffect, Fragment, type CSSProperties, type ReactElement } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { InteractiveSafeKeyboardSensor, InteractiveSafePointerSensor } from '@/utils/dndSensors';
import { useAuth } from '@/hooks/useAuth';
import { useDashboardStats, useSomethingThatWorked } from '@/hooks/useDashboardStats';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { CLIENT_CHECKLIST_ITEMS } from '@/services/onboarding.service';
import { PageHeader } from '@/components/layout/PageHeader';
import { DashboardFooter } from '@/components/layout/DashboardFooter';
import { BulkImportDialog } from '@/components/bulk/BulkImportDialog';
import { CanvaImportDialog } from '@/components/canva';
import { CreatePostSplitButton } from '@/components/ui';

// Dashboard components
import { PeriodPills } from '@/components/dashboard/PeriodPills';
import { StatsBox } from '@/components/dashboard/StatsBox';
import { DashboardSection } from '@/components/dashboard/DashboardSection';
import { SectionTitle } from '@/components/dashboard/SectionTitle';
import { PublishingSection } from '@/components/dashboard/PublishingSection';
import { MappedSection } from '@/components/dashboard/MappedSection';
import { PublishedSection } from '@/components/dashboard/PublishedSection';
import { ReviewRemixSection } from '@/components/dashboard/ReviewRemixSection';
import { SomethingWorkedSection } from '@/components/dashboard/SomethingWorkedSection';
import { AIProfilesSection } from '@/components/dashboard/AIProfilesSection';
import { TrendSuggestionsSection } from '@/components/dashboard/TrendSuggestionsSection';
import { WeeklyGoalsSection } from '@/components/dashboard/WeeklyGoalsSection';
import { AIInsightsWidget } from '@/components/dashboard/AIInsightsWidget';
import { FeatureGate } from '@/components/gating/FeatureGate';
import { LockedFeatureCard } from '@/components/gating/LockedFeatureCard';
import { useEntitlement } from '@/hooks/useEntitlement';
import { ReleaseSectionsRow, ReleaseSectionsError } from '@/components/dashboard/release/ReleaseSectionsRow';
import { useDashboardReleaseSections } from '@/hooks/useReleaseSections';
import { buildBoardReleaseHref } from '@/utils/releaseLinks';
import { useAIInsights } from '@/hooks/useAnalyticsWidgets';
import { useDashboardStore } from '@/stores/dashboardStore';
import { usePostsStore } from '@/stores/postsStore';
import { usePostCreatorStore } from '@/stores/postCreatorStore';
import { ICONS } from '@/constants/icons';
import { Tooltip } from '@/components/ui/Tooltip';
import { Icon } from '@/components/ui/Icon';
import { useTimezone } from '@/contexts/TimezoneContext';
import { useTrendSuggestions, useRefreshTrends } from '@/hooks/useTrendSuggestions';

/**
 * Dashboard Page
 *
 * Main authenticated page with customizable, draggable row-based sections.
 * Each draggable unit is a full row (which may contain multiple columns internally).
 *
 * Row order (default; a user's saved order always wins):
 *   0. published-worked  — Daily Brief: Published (2/3) + Something That Worked (1/3)
 *   1. pipeline          — "Your Asset Pipeline" title + stats grid
 *   2. release-reviewer / release-operator — what the viewer has to act on (hidden when not applicable)
 *   3. goals             — Weekly Goals (full width)
 *   4. trends            — Industry Trends (full width)
 *   5. remix             — Remix - Quick Sendout Opportunities (full width)
 *   6. ai-profiles       — AI Brand Profiles (full width)
 *   7. ai-insights       — AI Insights (auto-hidden when empty)
 *   8. mapped-publishing — Mapped & Ready to Commit (50%) + Publishing (50%)
 */

type Period = 'today' | 'week' | 'month';

interface ReleasePipelineTile {
  key: string;
  title: string;
  value: number;
  icon: string;
  accentColor: 'draft' | 'ready' | 'mapped' | 'scheduled' | 'published';
}

/**
 * Header actions for the Trends section - "Updated X" text + refresh button
 */
const TrendHeaderActions = () => {
  const { t } = useTranslation('dashboard');
  const { formatRelativeDate } = useTimezone();
  const { data } = useTrendSuggestions();
  const refreshMutation = useRefreshTrends();

  if (!data?.lastGenerated) return null;

  return (
    <div className="flex items-center gap-2 flex-shrink-0">
      <span className="hidden sm:inline text-xs text-gray-400">
        {t('dashboard_trends_updated', { defaultValue: 'Updated {{date}}', date: formatRelativeDate(data.lastGenerated) })}
      </span>
      <Tooltip content={data.needsRefresh ? t('dashboard_trends_refresh', { defaultValue: 'Refresh trends' }) : t('dashboard_trends_fresh', { defaultValue: 'Trends are fresh (refreshable every 24h)' })}>
        <button
          onClick={() => refreshMutation.mutate()}
          disabled={refreshMutation.isPending}
          className="w-8 h-8 flex items-center justify-center rounded-md border border-gray-200 text-gray-400 hover:text-primary-600 hover:bg-primary-50 hover:border-primary-200 transition-colors disabled:opacity-50"
        >
          <Icon name={ICONS.REFRESH} size="sm" ariaHidden className={refreshMutation.isPending ? 'animate-spin' : ''} />
        </button>
      </Tooltip>
    </div>
  );
};

// Sortable wrapper for dashboard rows
interface SortableSectionProps {
  id: string;
  children: React.ReactNode;
}

const SortableSection = ({ id, children }: SortableSectionProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  return (
    <div ref={setNodeRef} style={style} className="group/dashboard-row" {...attributes} {...listeners}>
      {children}
    </div>
  );
};

export const Dashboard = () => {
  const { t } = useTranslation('dashboard');
  const { user } = useAuth();
  const { teamId } = useEffectiveTeamId();
  const navigate = useNavigate();
  const postsStore = usePostsStore();
  const postCreatorStore = usePostCreatorStore();
  const [period, setPeriod] = useState<Period>('week');
  const { data: stats, isLoading: statsLoading } = useDashboardStats(period);
  const { data: somethingWorkedData } = useSomethingThatWorked();
  const { data: aiInsightsData } = useAIInsights();
  const releaseSections = useDashboardReleaseSections();
  const goalsEntitlement = useEntitlement('weekly_goals');
  const trendsEntitlement = useEntitlement('trend_suggestions');
  // Lara insights really locked (enforce): the slot stays as a slim locked teaser instead of vanishing
  const laraInsightsLocked = useEntitlement('lara_insights').locked;
  const { sections, toggleCollapse, reorder, getSortedSections } = useDashboardStore(teamId);
  // Hide the AI Insights row entirely on Dashboard until there's a generated payload —
  // the empty state lives on the Analytics page where users go to interact with it.
  const hasAIInsightsContent = (aiInsightsData?.[0]?.insights?.summary?.length ?? 0) > 0;
  // Independent collapse state for paired section cards
  const [publishedCollapsed, setPublishedCollapsed] = useState(false);
  const [workedCollapsed, setWorkedCollapsed] = useState(false);
  const [mappedCollapsed, setMappedCollapsed] = useState(false);
  const [publishingCollapsed, setPublishingCollapsed] = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [canvaImportOpen, setCanvaImportOpen] = useState(false);
  const { progress, completeChecklistItem } = useOnboarding();

  // Auto-complete "explore_dashboard" checklist item for agency clients on visit
  useEffect(() => {
    if (
      progress &&
      !progress.checklistItems?.[CLIENT_CHECKLIST_ITEMS.EXPLORE_DASHBOARD] &&
      progress.showChecklist &&
      progress.isAgencyClient
    ) {
      completeChecklistItem(CLIENT_CHECKLIST_ITEMS.EXPLORE_DASHBOARD).catch(() => {
        // Silently fail - non-critical
      });
    }
  }, [progress, completeChecklistItem]);

  // DnD sensors
  const sensors = useSensors(
    useSensor(InteractiveSafePointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(InteractiveSafeKeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  if (!user) {
    return null;
  }

  // Redirect agency staff users to the /agency dashboard.
  // Allow them to stay when in God Mode (they explicitly entered a workspace).
  // Exception: when a super admin is impersonating the staff user, ignore any
  // pre-existing God Mode session that belongs to the real user's prior state
  // and always redirect to /agency so the admin sees the correct portfolio view.
  const isImpersonating = !!user.impersonationSession;
  if (user.isAgencyStaff && (isImpersonating || !user.godModeSession)) {
    return <Navigate to="/agency/portfolio" replace />;
  }

  const handleCreatePost = () => {
    postCreatorStore.open();
  };

  // Get row collapsed state
  const isCollapsed = (rowId: string) => {
    return sections.find((s) => s.id === rowId)?.collapsed ?? false;
  };

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      reorder(active.id as string, over.id as string);
    }
  };

  // Get sorted row IDs for drag-and-drop, dropping rows that should hide entirely
  // Release rows exist only for the viewer's roles in a brand whose release workflow is on.
  const releaseData = releaseSections.data;
  const showReleaseReviewer =
    releaseSections.isError || (releaseData?.workflowActive === true && releaseData.viewer?.isReviewer === true);
  const showReleaseOperator =
    !releaseSections.isError && releaseData?.workflowActive === true && releaseData.viewer?.isCreator === true;

  // Asset Pipeline release stages (between Drafts and Ready to Shoot): same query + same numbers as the release rows. Only when the
  // workflow is on, by role; nothing while loading or on error (the error card handles retry).
  const releasePipelineTiles: ReleasePipelineTile[] = [];
  if (!releaseSections.isError && releaseData?.workflowActive === true) {
    const sec = releaseData.sections;
    const isReviewer = releaseData.viewer?.isReviewer === true;
    const isCreator = releaseData.viewer?.isCreator === true;
    const awaitingForYou = sec?.awaiting?.total ?? sec?.changesMade?.total ?? 0;
    if (isReviewer && !isCreator) {
      // A reviewer-only view has no all-items In review count; show its actionable subset.
      releasePipelineTiles.push(
        { key: 'awaiting', title: t('release_section_awaiting_title'), value: awaitingForYou, icon: ICONS.REVIEW, accentColor: 'mapped' },
      );
    }
    if (isCreator) {
      releasePipelineTiles.push(
        {
          key: 'in_review',
          title: t('release_pipeline_in_review'),
          value: sec?.inReview?.total ?? releaseData.counts?.inReview ?? 0,
          icon: ICONS.REVIEW,
          accentColor: 'scheduled',
        },
        { key: 'changes_requested', title: t('release_section_changes_requested_title'), value: sec?.changesRequested?.total ?? 0, icon: ICONS.CAUTION, accentColor: 'draft' },
        { key: 'ready_to_schedule', title: t('release_section_ready_title'), value: sec?.readyToSchedule?.total ?? 0, icon: ICONS.CALENDAR, accentColor: 'ready' },
      );
    }
  }

  // Pipeline cells. Release OFF: the exact original DOM (no wrappers, tiles carry their own divider).
  // Release ON: hairline-bordered flex cells; the tile fills the cell so its content stays centred.
  const releaseFlow = releasePipelineTiles.length > 0;
  // Equal columns from the tile count n: mobile 2 cols; sm-lg two balanced rows (ceil(n/2) cols);
  // xl one row (n cols). A short last row is closed by the last cell spanning the remaining columns.
  const flowCount = 5 + releasePipelineTiles.length;
  const flowColsSm = Math.ceil(flowCount / 2);
  const flowGridVars = {
    '--cols-sm': flowColsSm,
    '--cols-xl': flowCount,
    '--span-sm': flowColsSm - (flowCount - flowColsSm) + 1,
  } as CSSProperties;
  const renderCell = (
    key: string,
    isRelease: boolean,
    build: (cell: { hasDivider?: boolean; className?: string }) => ReactElement
  ): ReactElement => {
    if (!releaseFlow) return <Fragment key={key}>{build({ hasDivider: key !== 'published' })}</Fragment>;
    return (
      <div
        key={key}
        className={`relative flex border-t border-l border-gray-900/10 -mt-px -ml-px ${
          isRelease ? 'bg-slate-50' : ''
        } ${
          key === 'published'
            ? `${flowCount % 2 === 1 ? 'col-span-2' : ''} sm:[grid-column:span_var(--span-sm)] xl:col-span-1`
            : ''
        }`}
      >
        {build({ className: 'w-full' })}
      </div>
    );
  };
  const visibleSections = getSortedSections().filter((s) => {
    if (s.id === 'ai-insights' && !hasAIInsightsContent && !laraInsightsLocked) return false;
    if (s.id === 'release-reviewer' && !showReleaseReviewer) return false;
    if (s.id === 'release-operator' && !showReleaseOperator) return false;
    return true;
  });
  const sortedSections = visibleSections;
  const sectionIds = visibleSections.map((s) => s.id);

  /**
   * Render a dashboard row by its row ID.
   * Each case returns a full-width block that represents one draggable row.
   */
  const renderSection = (rowId: string): React.ReactNode => {
    switch (rowId) {
      // Release: what the reviewer has to release (or an error card with a manual retry)
      case 'release-reviewer':
        return releaseSections.isError ? (
          <ReleaseSectionsError onRetry={releaseSections.refetch} />
        ) : (
          <ReleaseSectionsRow group="reviewer" sections={releaseData?.sections} isNew={releaseSections.isNew} showHeading />
        );

      // Release: what the operator has to schedule or rework
      case 'release-operator':
        return (
          <ReleaseSectionsRow
            group="operator"
            sections={releaseData?.sections}
            isNew={releaseSections.isNew}
            showHeading={!showReleaseReviewer}
            showInReview={releaseData?.viewer?.isReviewer !== true}
          />
        );

      // Row 1: Published (2/3) + Something That Worked (1/3), or full-width if no top performers
      case 'published-worked': {
        const hasWorkedData = (somethingWorkedData?.topPerformers?.length ?? 0) > 0;
        return (
          <div className="space-y-4">
            <SectionTitle title={t('dashboard_section_daily_brief', { defaultValue: 'Daily Brief' })} />
            {hasWorkedData ? (
          <div className={`grid grid-cols-1 lg:grid-cols-3 gap-6 ${publishedCollapsed || workedCollapsed ? 'items-start' : ''}`}>
            <div className="order-last lg:order-first lg:col-span-2">
              <DashboardSection
                id="published"
                title={t('dashboard_section_published', { defaultValue: 'Published' })}
                icon={ICONS.CHECK_CIRCLE}
                collapsed={publishedCollapsed}
                onToggleCollapse={() => setPublishedCollapsed((v) => !v)}
              >
                <PublishedSection />
              </DashboardSection>
            </div>
            <div className="order-first lg:order-last lg:col-span-1 min-h-0">
              <DashboardSection
                id="something-worked"
                title={t('dashboard_something_worked_title', { defaultValue: 'This worked! Do it again.' })}
                icon={ICONS.STAR}
                collapsed={workedCollapsed}
                onToggleCollapse={() => setWorkedCollapsed((v) => !v)}
                className={workedCollapsed ? '' : 'h-full flex flex-col'}
              >
                <SomethingWorkedSection />
              </DashboardSection>
            </div>
          </div>
        ) : (
          <DashboardSection
            id="published"
            title={t('dashboard_section_published', { defaultValue: 'Published' })}
            icon={ICONS.CHECK_CIRCLE}
            collapsed={publishedCollapsed}
            onToggleCollapse={() => setPublishedCollapsed((v) => !v)}
          >
            <PublishedSection />
          </DashboardSection>
        )}
          </div>
        );
      }

      // Row 2: Weekly Goals
      case 'goals':
        return (
          <FeatureGate
            feature="weekly_goals"
            fallback={
              <div className="space-y-4">
                <SectionTitle title={t('dashboard_section_goals', { defaultValue: 'Weekly Goals' })} />
                <LockedFeatureCard feature="weekly_goals" upsellPlanSlug={goalsEntitlement.upsellPlanSlug} compact />
              </div>
            }
          >
            <WeeklyGoalsSection />
          </FeatureGate>
        );

      // Row 3: Industry Trends
      case 'trends':
        return (
          <div className="space-y-4">
            <SectionTitle title={t('dashboard_section_get_unstuck', { defaultValue: 'Get Unstuck' })} />
            <DashboardSection
              id="trends"
              title={t('dashboard_section_trends', { defaultValue: 'Trending in Your Industry' })}
              icon={ICONS.TREND_UP}
              collapsed={isCollapsed('trends')}
              onToggleCollapse={() => toggleCollapse('trends')}
              headerActions={trendsEntitlement.allowed ? <TrendHeaderActions /> : undefined}
            >
              <FeatureGate feature="trend_suggestions" compact>
                <TrendSuggestionsSection />
              </FeatureGate>
            </DashboardSection>
          </div>
        );

      // Row 4: Remix - Quick Sendout Opportunities
      case 'remix':
        return (
          <DashboardSection
            id="remix"
            title={t('dashboard_section_remix', { defaultValue: 'Remix' })}
            icon={ICONS.REFRESH}
            collapsed={isCollapsed('remix')}
            onToggleCollapse={() => toggleCollapse('remix')}
          >
            <ReviewRemixSection />
          </DashboardSection>
        );

      // Row 5: AI Brand Profiles (desktop only)
      case 'ai-profiles':
        return (
          <div className="hidden lg:block">
            <DashboardSection
              id="ai-profiles"
              title={t('dashboard_section_ai_profiles', { defaultValue: 'Brand Worlds' })}
              icon={ICONS.WORLD}
              collapsed={isCollapsed('ai-profiles')}
              onToggleCollapse={() => toggleCollapse('ai-profiles')}
            >
              <AIProfilesSection />
            </DashboardSection>
          </div>
        );

      // Row 6: "Your Asset Pipeline" title + stats grid (no DashboardSection wrapper)
      case 'pipeline':
        return (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <SectionTitle
                title={t('dashboard_section_pipeline', { defaultValue: 'Your Asset Pipeline' })}
              />
              <PeriodPills value={period} onChange={setPeriod} />
            </div>
            {/* One flow: Drafts -> release stages (when the workflow is on) -> Ready ... Published */}
            <div
              className={`bg-white border border-gray-900/10 rounded-xl overflow-hidden grid ${
                releaseFlow
                  ? 'grid-cols-2 sm:[grid-template-columns:repeat(var(--cols-sm),minmax(0,1fr))] xl:[grid-template-columns:repeat(var(--cols-xl),minmax(0,1fr))]'
                  : 'grid-cols-3 sm:grid-cols-5'
              }`}
              style={releaseFlow ? flowGridVars : undefined}
            >
              {renderCell('drafts', false, (cell) =>
              <StatsBox
                {...cell}
                title={t('dashboard_stats_drafts', { defaultValue: 'Drafts' })}
                value={stats?.drafts ?? 0}
                icon={ICONS.DRAFT}
                accentColor="draft"
                isLoading={statsLoading}
                onClick={() => {
                  postsStore.setFilter('contentStatus', 'DRAFT');
                  navigate('/creator');
                }}
              />
              )}
              {releasePipelineTiles.map((tile) => renderCell(`release-${tile.key}`, true, (cell) =>
                <StatsBox
                  {...cell}
                  title={tile.title}
                  value={tile.value}
                  icon={tile.icon}
                  accentColor={tile.accentColor}
                  onClick={() => navigate(buildBoardReleaseHref())}
                />
              ))}
              {renderCell('ready', false, (cell) =>
              <StatsBox
                {...cell}
                title={t('dashboard_stats_ready_to_shoot', { defaultValue: 'Ready to Shoot' })}
                value={stats?.readyToShoot ?? 0}
                icon={ICONS.ROCKET}
                accentColor="ready"
                isLoading={statsLoading}
                onClick={() => {
                  postsStore.setFilter('contentStatus', 'READY');
                  navigate('/calendar');
                }}
              />
              )}
              {renderCell('mapped', false, (cell) =>
              <StatsBox
                {...cell}
                title={t('dashboard_stats_mapped_ready', { defaultValue: 'Mapped & Ready' })}
                value={stats?.mappedAndReady ?? 0}
                icon={ICONS.CALENDAR}
                accentColor="mapped"
                isLoading={statsLoading}
                onClick={() => {
                  postsStore.setFilter('contentStatus', 'READY');
                  postsStore.setFilter('publishingStatus', 'mapped');
                  navigate('/calendar');
                }}
              />
              )}
              {renderCell('scheduled', false, (cell) =>
              <StatsBox
                {...cell}
                title={t('dashboard_stats_scheduled', { defaultValue: 'Scheduled' })}
                value={stats?.publishing?.[period] ?? 0}
                icon={ICONS.CLOCK}
                accentColor="scheduled"
                isLoading={statsLoading}
                onClick={() => navigate('/calendar')}
              />
              )}
              {renderCell('published', false, (cell) =>
              <StatsBox
                {...cell}
                title={t('dashboard_stats_published', { defaultValue: 'Published' })}
                value={stats?.published?.[period] ?? 0}
                icon={ICONS.CHECK_CIRCLE}
                accentColor="published"
                isLoading={statsLoading}
                onClick={() => navigate('/calendar')}
              />
              )}
            </div>
          </div>
        );

      // Row 7: AI Insights — only renders when payload exists (filtered upstream in visibleSections)
      case 'ai-insights':
        return <AIInsightsWidget />;

      // Row 8: Mapped & Ready to Commit (50%) + Publishing (50%)
      case 'mapped-publishing':
        return (
          <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${mappedCollapsed || publishingCollapsed ? 'items-start' : ''}`}>
            <DashboardSection
              id="mapped"
              title={t('dashboard_section_mapped', { defaultValue: 'Mapped & Ready to Commit' })}
              icon={ICONS.CALENDAR}
              collapsed={mappedCollapsed}
              onToggleCollapse={() => setMappedCollapsed((v) => !v)}
            >
              <MappedSection />
            </DashboardSection>
            <DashboardSection
              id="publishing"
              title={t('dashboard_section_publishing', { defaultValue: 'Publishing' })}
              icon={ICONS.SHARE}
              collapsed={publishingCollapsed}
              onToggleCollapse={() => setPublishingCollapsed((v) => !v)}
            >
              <PublishingSection />
            </DashboardSection>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title={t('dashboard_title')}
        subtitle={t('dashboard_subtitle')}
        icon={ICONS.DASHBOARD}
        headerType="dashboard"
        documentTitle="Dashboard"
      />

      {/* Welcome Message */}
      <div>
        <div className="flex items-start justify-between gap-2 sm:gap-4">
          <div className="min-w-0 flex-1 max-w-[60%] sm:max-w-none">
            <h2 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
              {t('dashboard_greeting', { name: user?.fullName ? user.fullName.split(' ')[0] : 'there' })}
            </h2>
            <p className="text-sm sm:text-base text-gray-600 mt-1">{t('dashboard_tagline')}</p>
          </div>

          {/* Create Post Split Button */}
          <div className="flex-shrink-0 mt-0.5">
            <CreatePostSplitButton
              onCreatePost={handleCreatePost}
              onBulkImport={() => setBulkImportOpen(true)}
              onCanvaImport={() => setCanvaImportOpen(true)}
            />
          </div>
        </div>
      </div>

      {/* Draggable Rows */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={sectionIds} strategy={verticalListSortingStrategy}>
          <div className="space-y-6">
            {sortedSections.map((section) => (
              <SortableSection key={section.id} id={section.id}>
                {renderSection(section.id)}
              </SortableSection>
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {/* Inspirational Footer */}
      <DashboardFooter />

      {/* Bulk Import Dialog */}
      <BulkImportDialog
        isOpen={bulkImportOpen}
        onClose={() => setBulkImportOpen(false)}
        onSuccess={() => {
          setBulkImportOpen(false);
        }}
      />

      {/* Canva Import Dialog */}
      <CanvaImportDialog
        isOpen={canvaImportOpen}
        onClose={() => setCanvaImportOpen(false)}
        onSuccess={() => {
          setCanvaImportOpen(false);
        }}
      />
    </div>
  );
};
